/* ============================================================
   TREASURE OF INDIA — polish.js  (v68 finishing pass)
   ------------------------------------------------------------
   1. Parallax banners        (transform + requestAnimationFrame)
   2. Extended scroll-reveal  (headings, states, stories, banner copy)
   3. Brush-stroke underline  (adds classes; CSS animates)

   Rules honoured:
   - vanilla JS + IntersectionObserver only, no libraries
   - transforms/opacity only, passive listeners, rAF-throttled
   - prefers-reduced-motion  -> nothing runs at all
   - Shopify theme editor    -> re-inits on section:load and
     reveals instantly on section:select (preview never blanks)
   To revert: delete this file + its include line in theme.liquid.
   ============================================================ */
(function () {
  "use strict";

  var mqReduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)");
  var REDUCED = mqReduce && mqReduce.matches;
  var HAS_IO = "IntersectionObserver" in window;
  var DESIGN_MODE = !!(window.Shopify && window.Shopify.designMode);

  if (REDUCED || !HAS_IO) return;              /* CSS fallbacks show everything statically */

  document.documentElement.classList.add("js-polish");

  /* ============================================================
     [1] PARALLAX BANNERS
     Targets every .image-banner with a background photo
     (Masterpieces / Bespoke / any image-with-text-overlay banner).
     The photo is pre-scaled in polish.css; here we slide it with
     translate3d as the section crosses the viewport.
       progress: -1 (section below fold) … 0 (centred) … +1 (above)
       shift   : progress * amplitude
     Desktop amplitude ±9% of banner height, mobile a gentle ±3%.
     ============================================================ */
  (function parallax() {
    var mqMobile = window.matchMedia("(max-width: 720px)");
    var items = [];
    var scheduled = false;
    var vh = window.innerHeight;

    function collect(scope) {
      (scope || document).querySelectorAll(".image-banner").forEach(function (sec) {
        if (sec.dataset.pxInit) return;
        var img = sec.querySelector(".sec-bg-img");
        if (!img) return;                       /* no photo -> decorative backdrop, skip */
        sec.dataset.pxInit = "1";
        img.classList.add("px-img");
        items.push({ sec: sec, img: img, on: false });
      });
      measure();
      observeAll();
    }

    /* only animate banners that are actually on screen */
    var vis = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        var it = items.find(function (i) { return i.sec === en.target; });
        if (it) it.on = en.isIntersecting;
      });
      schedule();
    }, { rootMargin: "80px 0px" });

    function observeAll() {
      items.forEach(function (it) {
        if (!it.observed) { it.observed = true; vis.observe(it.sec); }
      });
    }

    /* v105 — measure once, not every frame.
       This used to call getBoundingClientRect() per banner per frame. Each
       call forces the browser to flush pending layout, so on a page with
       several banners the main thread did a full layout on every scroll
       frame and the whole page stuttered — worst on mid-range phones. The
       geometry only changes on resize or when images finish loading, so it
       is cached and the scroll position alone drives the transform. */
    function measure() {
      var sy = window.pageYOffset;
      items.forEach(function (it) {
        var r = it.sec.getBoundingClientRect();
        it.top = r.top + sy;
        it.h = r.height;
      });
    }

    function frame() {
      scheduled = false;
      var mobile = mqMobile.matches;
      /* a 1.2 scale on a full-bleed photo is a very large composited layer;
         phones do not need the extra travel to read as depth */
      var scaleStr = mobile ? " scale(1.06)" : " scale(1.18)";
      var ampPct = mobile ? 0.02 : 0.09;
      var sy = window.pageYOffset;
      items.forEach(function (it) {
        if (!it.on || !it.h) return;
        var top = it.top - sy;                       /* cached, no layout read */
        var progress = (top + it.h / 2 - vh / 2) / ((vh + it.h) / 2);
        progress = Math.max(-1, Math.min(1, progress));
        var shift = (-progress * ampPct * it.h).toFixed(1);
        it.img.style.transform = "translate3d(0," + shift + "px,0)" + scaleStr;
      });
    }
    function schedule() {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(frame);
    }

    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", function () {
      vh = window.innerHeight; measure(); schedule();
    }, { passive: true });
    /* banner photos load after first paint and change the section height */
    window.addEventListener("load", function () { measure(); schedule(); });

    /* live toggle if the user flips reduced-motion mid-session */
    if (mqReduce && mqReduce.addEventListener) {
      mqReduce.addEventListener("change", function (e) {
        if (e.matches) items.forEach(function (it) { it.img.style.transform = ""; });
      });
    }

    collect();
    document.addEventListener("shopify:section:load", function (e) { collect(e.target); });
  })();

  /* ============================================================
     [2] EXTENDED SCROLL-REVEAL (fade-up 24px / 0.6s ease-out)
     theme.js already covers product/blog/circle/craft cards.
     This adds the pieces the brief calls out that were missing:
       - section heading blocks (eyebrow + h2 + subtext together)
       - state cards, story cards, iconic-piece cards
       - banner copy (eyebrow / heading / text / CTA)
     Children stagger 90ms *within their own group*, so two
     sections never share a delay chain. Elements theme.js
     already animates (.anim-item) are skipped — no double fade.
     ============================================================ */
  (function reveal() {

    /* group selector -> children revealed with stagger */
    var GROUPS = [
      { root: ".ftabs-head",            kids: ":scope > *" },
      { root: ".stories-head",          kids: ":scope > *" },
      { root: ".ip-head",               kids: ":scope > *" },
      { root: ".sv-head",               kids: ":scope > *" },
      { root: ".section-head",          kids: ":scope > *" },
      { root: ".circle-cats .wrap",     kids: ":scope > .eyebrow, :scope > h2, :scope > .cc-sub" },
      { root: ".image-banner__inner",   kids: ":scope > *" },
      { root: ".states-grid",           kids: ".state-item" },
      { root: ".stories-grid",          kids: ".story-card" },
      /* v107: was ".ip-row". That row is width:max-content — about 5000px
         wide — so the share of it inside the viewport on a phone is ~7.8%,
         just under this observer's 0.08 threshold. The callback never fired
         and every card stayed at opacity:0, leaving an empty section.
         .ip-viewport is the scroll container, so it is viewport-width and
         crosses the threshold normally. The cards are still the kids. */
      { root: ".ip-viewport",           kids: ".ip-card, .ip-endcard" }
    ];
    var STAGGER = 90;   /* ms, brief asks 80–100 */

    /* WHY THIS WAS OUT OF SEQUENCE (v105)
       Every child used to be observed on its own, and its delay was baked in
       at prep time as (index % 6) * 90ms. So the delay described where an
       element sat in the DOM, not when it actually reached the viewport. Two
       visible symptoms followed: scrolling fast made items land in DOM order
       rather than the order they appeared, and an item entering alone still
       waited out a delay it inherited from its position — the fourth card in
       a row would hang for 270ms for no reason a visitor could see.

       Now the GROUP is observed, not the children. When a group arrives its
       children are revealed together, in DOM order, and the stagger is applied
       at that moment. One entry point, one sequence, every time. It also cuts
       the number of observed elements by roughly the group size, which helps
       the scrolling itself. */
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        var root = en.target;
        io.unobserve(root);
        var kids = root.__prKids || [];
        kids.forEach(function (el, i) {
          el.style.transitionDelay = (i * STAGGER) + "ms";
          el.classList.add("pr-in");
        });
        /* the delay has done its job; leaving it set makes any later
           transition on the element (hover, for one) lag by the same amount */
        setTimeout(function () {
          kids.forEach(function (el) { el.style.transitionDelay = ""; });
        }, kids.length * STAGGER + 700);
      });
    }, { rootMargin: "0px 0px -8% 0px", threshold: 0.08 });

    function prep(scope) {
      GROUPS.forEach(function (g) {
        (scope || document).querySelectorAll(g.root).forEach(function (root) {
          if (root.dataset.prDone) return;
          root.dataset.prDone = "1";
          var kids = [];
          root.querySelectorAll(g.kids).forEach(function (el) {
            if (el.classList.contains("anim-item")) return;   /* theme.js owns it */
            el.classList.add("pr-item");
            kids.push(el);
          });
          if (!kids.length) return;
          root.__prKids = kids;
          if (DESIGN_MODE) {                                  /* editor: never hide */
            kids.forEach(function (el) { el.classList.add("pr-in"); });
            return;
          }
          io.observe(root);
        });
      });
    }

    prep();
    document.addEventListener("shopify:section:load", function (e) { prep(e.target); });
    /* merchant clicks a section/block in the editor: reveal instantly */
    document.addEventListener("shopify:section:select", function (e) {
      e.target.querySelectorAll(".pr-item").forEach(function (el) { el.classList.add("pr-in"); });
    });
  })();

  /* ============================================================
     [3] BRUSH-STROKE HEADING UNDERLINE
     Adds .brush-hu to the key section headings; when the heading
     scrolls into view we add .hu-in and CSS sweeps the stroke
     from 0 -> 100% (scaleX — transform only). Applies to section
     headings only; banner titles (.image-banner__title) sit on
     photography and are deliberately left alone.
     ============================================================ */
  (function brush() {
    var HEADS = [
      ".ftabs-head h2",
      ".stories-head h2",
      ".ip-head h2",
      ".sv-head h2",
      ".section-head h2",
      ".circle-cats .wrap > h2"
    ].join(",");

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        en.target.classList.add("hu-in");
        io.unobserve(en.target);
      });
    }, { threshold: 0.6 });

    function prep(scope) {
      (scope || document).querySelectorAll(HEADS).forEach(function (h) {
        if (h.classList.contains("brush-hu")) return;
        h.classList.add("brush-hu");
        if (DESIGN_MODE) { h.classList.add("hu-in"); return; }
        io.observe(h);
      });
    }

    prep();
    document.addEventListener("shopify:section:load", function (e) { prep(e.target); });
  })();

})();

/* ============================================================
   [F1] STOCK-AWARE QUANTITY (v71) — functional, always runs
   (deliberately OUTSIDE the reduced-motion gate above).
   Most pieces are one-of-a-kind: stock = 1 and never restocked.
   - .qty--solo (set in Liquid) hides the stepper when the
     current variant has only 1 available.
   - On variant change, read data-qmax from the selected option:
     show/hide the stepper and clamp the quantity input.
   - Clamps the + button to real stock (theme.js caps at 99).
   ============================================================ */
(function () {
  "use strict";
  var form = document.querySelector("form[data-product-form]");
  if (!form) return;
  var wrap = form.querySelector("[data-qty-wrap]");
  var input = wrap ? wrap.querySelector("input[name=quantity]") : null;
  var sel = form.querySelector("#variant-select");
  if (!wrap || !input) return;

  function clamp() {
    var m = parseInt(input.max, 10) || 99;
    var v = parseInt(input.value, 10) || 1;
    if (v > m) input.value = m;
    if (v < 1) input.value = 1;
  }
  function apply(max) {
    max = parseInt(max, 10);
    if (isNaN(max) || max < 1) max = 1;
    input.max = max;
    wrap.classList.toggle("qty--solo", max <= 1);
    if (max <= 1) input.value = 1;
    clamp();
  }

  if (sel) {
    sel.addEventListener("change", function () {
      var o = sel.options[sel.selectedIndex];
      apply(o ? o.getAttribute("data-qmax") : 99);
    });
  }
  /* runs after theme.js's own +/- handlers (bubbles up to the wrap) */
  wrap.addEventListener("click", clamp);
  input.addEventListener("change", clamp);
})();

/* ============================================================
   [F2] READ MORE for long descriptions (v75) — functional,
   always runs (outside the reduced-motion gate).
   Clamps a description only if it genuinely overflows, then
   injects an accessible toggle. Short descriptions are left
   exactly as they are, with no button.
   Targets: product description (PDP) + collection description.
   ============================================================ */
(function () {
  "use strict";

  var TARGETS = [
    { sel: ".pdp-desc--full", max: 280, lines: 6 },
    { sel: ".collection-desc", max: 96, lines: 4 }
  ];
  var SLACK = 48;   /* don't clamp for the sake of a stray line or two */
  var uid = 0;

  function build(el, cfg) {
    if (el.dataset.rmDone) return;

    /* measure uncollapsed; bail out if it comfortably fits */
    if (el.scrollHeight <= cfg.max + SLACK) return;

    el.dataset.rmDone = "1";
    if (!el.id) el.id = "rmDesc" + (++uid);
    /* how many whole lines of the opening paragraph stay visible */
    el.style.setProperty("--rm-lines", cfg.lines);
    el.classList.add("rm-clamp");

    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = "rm-toggle";
    btn.setAttribute("aria-expanded", "false");
    btn.setAttribute("aria-controls", el.id);
    btn.textContent = "Read more";
    el.insertAdjacentElement("afterend", btn);

    btn.addEventListener("click", function () {
      var open = el.classList.toggle("rm-open");
      btn.setAttribute("aria-expanded", open ? "true" : "false");
      btn.textContent = open ? "Read less" : "Read more";
      if (!open) {
        var top = el.getBoundingClientRect().top;
        if (top < 0) el.scrollIntoView({ block: "start", behavior: "smooth" });
      }
    });
  }

  function init(scope) {
    TARGETS.forEach(function (cfg) {
      (scope || document).querySelectorAll(cfg.sel).forEach(function (el) {
        build(el, cfg);
      });
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { init(); });
  } else {
    init();
  }
  /* images inside descriptions can change the height */
  window.addEventListener("load", function () { init(); });
  document.addEventListener("shopify:section:load", function (e) { init(e.target); });
})();

/* ============================================================
   [F3] LOAD MORE — collection pagination (v76), functional.
   Progressive enhancement:
     - No JS  -> numbered pagination still renders and works.
     - JS on  -> numbered links are hidden, the button fetches the
                 next page, appends its cards to the grid and
                 updates the URL (so refresh/back keeps position).
   Uses the existing markup only; no new libraries.
   ============================================================ */
(function () {
  "use strict";

  var box = document.querySelector("[data-load-more]");
  var grid = document.getElementById("productGrid");
  if (!box || !grid) return;

  var btn = box.querySelector("[data-load-more-btn]");
  var label = box.querySelector("[data-load-more-label]");
  var shown = box.querySelector("[data-shown]");
  var fallback = document.querySelector("[data-pagination-fallback]");
  var next = box.getAttribute("data-next-url");

  if (!btn || !next) return;

  /* JS is available: numbered pagination becomes redundant */
  if (fallback) fallback.hidden = true;

  var busy = false;
  var failed = false;

  function finish() {
    /* no more pages — drop the button, keep the count line */
    btn.remove();
  }

  btn.addEventListener("click", function () {
    if (busy || !next) return;
    busy = true;
    failed = false;
    btn.disabled = true;
    label.textContent = "Loading\u2026";

    fetch(next, { headers: { "X-Requested-With": "XMLHttpRequest" } })
      .then(function (r) {
        if (!r.ok) throw new Error(r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, "text/html");
        var newGrid = doc.getElementById("productGrid");
        if (!newGrid) throw new Error("no grid");

        var cards = [].slice.call(newGrid.children);
        var first = cards[0];
        cards.forEach(function (c) { grid.appendChild(c); });

        /* keep the running count honest */
        if (shown) shown.textContent = grid.children.length;

        /* address bar follows the last loaded page */
        try { history.replaceState(null, "", next); } catch (e) {}

        /* queue up the page after this one */
        var nextBox = doc.querySelector("[data-load-more]");
        next = nextBox ? nextBox.getAttribute("data-next-url") : "";
        if (!next) finish();

        /* move focus to the first new card for keyboard users */
        if (first) {
          var link = first.querySelector("a");
          if (link) { link.setAttribute("tabindex", "-1"); link.focus({ preventScroll: true }); }
        }

        /* let the reveal observer pick up the new cards */
        document.dispatchEvent(new CustomEvent("toi:products-appended", { detail: { cards: cards } }));
      })
      .catch(function () {
        failed = true;
        label.textContent = "Couldn't load more \u2014 try again";
        if (fallback) fallback.hidden = false;   /* give them the real links back */
      })
      .finally(function () {
        busy = false;
        btn.disabled = false;
        if (next && !failed) label.textContent = "Load more";
      });
  });
})();

/* ============================================================
   [F4] DRAWER SCROLL PROGRESS (v79) — functional, always runs.
   Vertical twin of the .tscroll-progress bars theme.js builds
   for the horizontal sliders, so the menu matches the rest of
   the site instead of showing the OS scrollbar.
   Same maths as the horizontal version: the fill grows from
   "viewport share" to 100% as you reach the bottom.
   ============================================================ */
(function () {
  "use strict";

  var drawer = document.querySelector(".drawer");
  if (!drawer || drawer.dataset.tspvDone) return;
  drawer.dataset.tspvDone = "1";

  var bar = document.createElement("div");
  bar.className = "tscroll-progress tscroll-progress--v is-hidden";
  bar.setAttribute("aria-hidden", "true");
  bar.appendChild(document.createElement("span"));
  drawer.insertBefore(bar, drawer.firstChild);
  var fill = bar.firstChild;

  var ticking = false;

  function update() {
    var max = drawer.scrollHeight - drawer.clientHeight;
    /* nothing to scroll — no bar at all */
    if (max <= 2) { bar.classList.add("is-hidden"); return; }
    bar.classList.remove("is-hidden");

    var h = drawer.clientHeight;
    bar.style.setProperty("--tsp-h", h + "px");

    var pct = drawer.scrollTop / max;
    var base = h / drawer.scrollHeight;          /* share of content on screen */
    bar.style.setProperty("--tsp-fill", (base + (1 - base) * pct) * h + "px");
  }

  drawer.addEventListener("scroll", function () {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () { ticking = false; update(); });
  }, { passive: true });

  window.addEventListener("resize", update, { passive: true });

  /* recalc when the drawer opens and when a submenu expands/collapses */
  document.querySelectorAll(".menu-toggle").forEach(function (b) {
    b.addEventListener("click", function () { setTimeout(update, 340); });
  });
  document.querySelectorAll(".drawer-sub-trigger").forEach(function (t) {
    t.addEventListener("click", function () { setTimeout(update, 60); });
  });

  update();
})();

/* ============================================================
   v93 — STORE PICKUP (PDP)
   ------------------------------------------------------------
   Two jobs:
     1. Open / close the store information drawer.
     2. Keep pickup in sync with the selected variant by asking
        Shopify to re-render sections/pickup-availability for the
        new variant id and swapping the markup in.

   The drawer is bound with delegation on the wrapper, so the
   handlers survive the markup being replaced in step 2.
   ============================================================ */
(function () {
  var wrap = document.querySelector("[data-pickup-wrap]");
  if (!wrap) return;

  var lastFocus = null;

  function drawer() { return wrap.querySelector("[data-pickup-drawer]"); }

  function onKey(e) {
    if (e.key === "Escape") closeDrawer();
  }

  function openDrawer() {
    var d = drawer();
    if (!d) return;
    lastFocus = document.activeElement;
    d.hidden = false;
    requestAnimationFrame(function () { d.classList.add("is-open"); });
    document.body.style.overflow = "hidden";
    var x = d.querySelector(".pickup-drawer__x");
    if (x) x.focus();
    document.addEventListener("keydown", onKey);
  }

  function closeDrawer() {
    var d = drawer();
    if (!d) return;
    d.classList.remove("is-open");
    document.body.style.overflow = "";
    document.removeEventListener("keydown", onKey);
    setTimeout(function () { d.hidden = true; }, 380);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  wrap.addEventListener("click", function (e) {
    if (e.target.closest("[data-pickup-open]")) { e.preventDefault(); openDrawer(); }
    else if (e.target.closest("[data-pickup-close]")) { e.preventDefault(); closeDrawer(); }
  });

  /* ---- refresh on variant change ---- */
  var select = document.querySelector("form[data-product-form] #variant-select");
  if (!select) return;

  var base = wrap.getAttribute("data-product-url");
  if (!base) return;

  var token = 0;

  select.addEventListener("change", function () {
    var id = select.value;
    if (!id) return;
    var mine = ++token;

    fetch(base + "?section_id=pickup-availability&variant=" + encodeURIComponent(id))
      .then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); })
      .then(function (html) {
        /* a slower earlier request must not overwrite a newer one */
        if (mine !== token) return;
        var doc = new DOMParser().parseFromString(html, "text/html");
        var fresh = doc.querySelector(".pickup");
        wrap.innerHTML = fresh ? fresh.outerHTML : "";
      })
      .catch(function () { /* leave the previous state in place */ });
  });
})();

/* ============================================================
   v109 — AUTO-SCROLL HINT FOR HORIZONTAL ROWS
   ------------------------------------------------------------
   Client feedback: people do not realise these rows scroll. The
   row now advances one card at a time so the movement itself is
   the affordance.

   The rules that keep this from becoming an irritation:
     - it only runs while the row is actually on screen
     - it stops the moment someone touches, drags, hovers, or
       focuses inside it, and does not resume
     - it pauses when the tab is hidden
     - it does nothing at all under prefers-reduced-motion, and
       nothing if the row does not actually overflow
     - it stops at the end rather than snapping back to the start,
       so nobody loses their place mid-read
   Opt in per section with data-autoscroll="<ms>".
   ============================================================ */
(function () {
  "use strict";
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  /* How far one move should travel.

     Some rows put the cards straight inside the scroll container
     (.ftabs-row > .ftabs-item), others wrap them in a track first
     (.ip-viewport > .ip-row > .ip-card). Measuring the direct child
     blindly returned the width of the WHOLE track on the second kind —
     2798px against a 260px card — so Iconic Pieces jumped to the end on
     its first move and then stopped, which read as broken. When the only
     child is wider than the container it is a track, so we look inside
     it for the real card. */
  function step(row) {
    var host = row;
    var kid = row.firstElementChild;
    if (kid && row.children.length === 1 && kid.getBoundingClientRect().width > row.clientWidth + 1) {
      host = kid;
      kid = kid.firstElementChild;
    }
    if (!kid) return 0;
    var cs = getComputedStyle(host);
    var gap = parseFloat(cs.columnGap === "normal" ? cs.gap : cs.columnGap) || 0;
    return Math.round(kid.getBoundingClientRect().width + gap);
  }

  function init(row) {
    if (row.dataset.asReady === "true") return;
    row.dataset.asReady = "true";

    var delay = parseInt(row.getAttribute("data-autoscroll"), 10) || 4000;
    /* held covers a deliberate interaction or keyboard focus. It is a pause
       with a timeout, not a permanent stop: the row is meant to keep going.
       visible is read by tick() rather than gating the timer, so an
       IntersectionObserver callback firing late cannot leave the row dead. */
    var timer = null, visible = false, held = false, resumeAt = null;

    function canScroll() { return row.scrollWidth - row.clientWidth > 8; }

    function tick() {
      if (held || !visible || document.hidden || !canScroll()) return;
      var d = step(row);
      if (!d) return;
      /* At the end, return to the start instead of stopping. A row that
         halts at the end reads as broken rather than finished. */
      if (row.scrollLeft + row.clientWidth >= row.scrollWidth - 4) {
        row.scrollTo({ left: 0, behavior: "smooth" });
        return;
      }
      row.scrollBy({ left: d, behavior: "smooth" });
    }

    /* The first move happens as soon as the row is on screen, not one full
       interval later. Waiting the full delay made the row look static on
       arrival, which is the opposite of the point: the movement IS the
       signal that it scrolls. A short beat lets the section paint first. */
    var primed = false;
    function start() {
      if (timer) return;
      if (!primed) {
        primed = true;
        setTimeout(function () { tick(); }, 350);
      }
      timer = setInterval(tick, delay);
    }
    function pause() { clearInterval(timer); timer = null; }
    /* Hold, not stop. Hovering no longer pauses at all — the row keeps
       moving while someone reads it. Deliberate interaction gets it out of
       the way, then it picks up again. */
    function hold(ms) {
      held = true;
      clearTimeout(resumeAt);
      resumeAt = setTimeout(function () { held = false; }, ms || Math.max(delay * 2, 6000));
    }

    /* Deliberate interaction with the row stops it for good. A plain wheel
       is NOT that: scrolling the page with the pointer happening to pass
       over the row used to kill it permanently, which is why it looked
       dead on a real page. Only a sideways wheel counts, since that is
       someone scrolling this row rather than the page. */
    ["pointerdown", "touchstart"].forEach(function (ev) {
      row.addEventListener(ev, function () { hold(); }, { passive: true });
    });
    row.addEventListener("wheel", function (e) {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) hold();
    }, { passive: true });
    row.addEventListener("keydown", function (e) {
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") hold();
    });
    row.addEventListener("focusin", function () { held = true; clearTimeout(resumeAt); });
    row.addEventListener("focusout", function () {
      if (!row.contains(document.activeElement)) hold(1200);
    });
    document.addEventListener("visibilitychange", function () {
      document.hidden ? pause() : start();
    });

    if ("IntersectionObserver" in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          visible = en.isIntersecting;
          visible ? start() : pause();
        });
      }, { threshold: 0.35 }).observe(row);
    } else {
      visible = true; start();
    }
  }

  /* Some rows are not scroll containers at all. The arch carousel moves a
     track with a CSS transform and owns its own index, looping and
     in-flight guard, so the honest way to advance it is to press its own
     next arrow rather than reimplement any of that here. Same guardrails
     as the scrolling rows. */
  function initClick(root) {
    if (root.dataset.asReady === "true") return;
    root.dataset.asReady = "true";

    var target = root.querySelector(root.getAttribute("data-autoscroll-click"));
    if (!target) return;

    var delay = parseInt(root.getAttribute("data-autoscroll"), 10) || 4000;
    var timer = null, visible = false, held = false, resumeAt = null;

    function tick() {
      if (held || !visible || document.hidden) return;
      target.click();
    }
    /* The first move happens as soon as the row is on screen, not one full
       interval later. Waiting the full delay made the row look static on
       arrival, which is the opposite of the point: the movement IS the
       signal that it scrolls. A short beat lets the section paint first. */
    var primed = false;
    function start() {
      if (timer) return;
      if (!primed) {
        primed = true;
        setTimeout(function () { tick(); }, 350);
      }
      timer = setInterval(tick, delay);
    }
    function pause() { clearInterval(timer); timer = null; }
    /* Hold, not stop. Hovering no longer pauses at all — the row keeps
       moving while someone reads it. Deliberate interaction gets it out of
       the way, then it picks up again. */
    function hold(ms) {
      held = true;
      clearTimeout(resumeAt);
      resumeAt = setTimeout(function () { held = false; }, ms || Math.max(delay * 2, 6000));
    }

    ["pointerdown", "touchstart"].forEach(function (ev) {
      root.addEventListener(ev, function () { hold(); }, { passive: true });
    });
    /* Keyboard focus is the one case where continuing is genuinely harmful:
       moving content out from under someone tabbing through it. Holding while
       focus is inside is also what keeps this within WCAG 2.2.2. */
    root.addEventListener("focusin", function () { held = true; clearTimeout(resumeAt); });
    root.addEventListener("focusout", function () {
      if (!root.contains(document.activeElement)) hold(1200);
    });
    document.addEventListener("visibilitychange", function () {
      document.hidden ? pause() : start();
    });

    if ("IntersectionObserver" in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { visible = en.isIntersecting; visible ? start() : pause(); });
      }, { threshold: 0.25 }).observe(root);
    } else { visible = true; start(); }
  }

  function boot(scope) {
    var root = scope || document;
    root.querySelectorAll("[data-autoscroll-click]").forEach(initClick);
    root.querySelectorAll("[data-autoscroll]:not([data-autoscroll-click])").forEach(init);
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { boot(); });
  } else { boot(); }
  /* Rows built from a feed (Instagram) fill in after first paint, so a row
     that could not scroll at boot may be able to a moment later. */
  window.addEventListener("load", function () { boot(); });
  document.addEventListener("shopify:section:load", function (e) { boot(e.target); });
})();
